import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istFehlendesSchema } from './sifaService';
import {
  FRIST_KATEGORIEN,
  KENNZAHLEN_LEER,
  TAETIGKEIT_ARTEN,
  type FristKategorie,
  type SifaFrist,
  type SifaKennzahlen,
  type SifaTaetigkeit,
  type TaetigkeitArt,
} from './betreuungTypes';
import { AMPEL_GELB_TAGE, isoDatum, naechsteFaelligkeit, parseIsoDatum, plusTage, type NeueFrist } from './fristenLogic';
import { DAUER_MAX_MINUTEN, type NeueTaetigkeit } from './taetigkeitenLogic';

export type { NeueFrist } from './fristenLogic';
export type { NeueTaetigkeit } from './taetigkeitenLogic';

/**
 * Datenbankzugriff der SiFa-Betreuung — Tabellen `sifa_fristen` und
 * `sifa_taetigkeiten` (supabase/sifa_betreuung.sql, MIGRATIONS.md Nr. 92).
 * Vertrag: docs/sifa-betreuung-plan.md §2; Paket D codiert dagegen.
 *
 * Muster sifaService.ts, mit zwei Riegeln:
 *
 * (a) ERLEDIGT WIRD GENAU EINMAL. `erledigeFrist` schreibt `erledigt_am`
 *     ausschliesslich `where erledigt_am is null` und zaehlt die Zeilen: null
 *     Zeilen heisst „schon erledigt" (zweiter Browser, Doppelklick) und ist
 *     ein Fehler, keine zweite Nachfolgezeile. Die Nachfolgezeile haengt per
 *     `vorgaenger_id` am Original; der partielle Unique-Index in Nr. 92 laesst
 *     davon hoechstens EINE zu — bei `23505` wird die vorhandene gelesen und
 *     zurueckgegeben.
 *
 * (b) GEBUCHTES BLEIBT. `deleteTaetigkeit` loescht nur `where gebucht_am is
 *     null`; Datum, Dauer und Art einer gebuchten Taetigkeit lassen sich nicht
 *     mehr aendern (Stufe 3 bucht auf eine LV-Position — ein nachtraeglich
 *     geaenderter Nachweis waere ein anderer Beleg).
 *
 * FAIL-OPEN: solange Nr. 92 nicht gefahren ist, liefern die Lesewege leere
 * Listen bzw. Null-Kennzahlen (42P01/PGRST205). Eine offene Migration darf
 * die Projektansicht nicht aufreissen. Schreibwege werfen.
 *
 * Alle Fehler gehen als `Error(describeError(e))` hinaus.
 */

const T_FRISTEN = 'sifa_fristen';
const T_TAETIGKEITEN = 'sifa_taetigkeiten';

// ─── Fehler ─────────────────────────────────────────────────────────────────

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/** 23505 = unique_violation, hier: `vorgaenger_id` hat schon eine Nachfolgezeile. */
const istUniqueVerletzung = (e: unknown): boolean => errorCode(e) === '23505';

const werfe = (e: unknown): never => {
  throw new Error(describeError(e));
};

// ─── Mapper snake → camel ───────────────────────────────────────────────────

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v, 0));
/** Datum/Zeitstempel ohne Umrechnung — die Anzeige formatiert selbst. */
const iso = (v: unknown): string => str(v);

export const mapFrist = (row: Zeile): SifaFrist => ({
  id: str(row.id),
  projectId: str(row.project_id),
  mitarbeiterId: strOrNull(row.mitarbeiter_id),
  kategorie: (str(row.kategorie) || 'sonstiges') as FristKategorie,
  titel: str(row.titel),
  faelligAm: iso(row.faellig_am),
  intervallMonate: numOrNull(row.intervall_monate),
  erledigtAm: strOrNull(row.erledigt_am),
  zustaendig: strOrNull(row.zustaendig),
  notiz: strOrNull(row.notiz),
  vorgaengerId: strOrNull(row.vorgaenger_id),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapTaetigkeit = (row: Zeile): SifaTaetigkeit => ({
  id: str(row.id),
  projectId: str(row.project_id),
  datum: iso(row.datum),
  dauerMinuten: num(row.dauer_minuten, 0),
  art: (str(row.art) || 'sonstiges') as TaetigkeitArt,
  titel: str(row.titel),
  beschreibung: strOrNull(row.beschreibung),
  durchgefuehrtVon: strOrNull(row.durchgefuehrt_von),
  createdBy: strOrNull(row.created_by),
  bezug: strOrNull(row.bezug),
  gebuchtAm: strOrNull(row.gebucht_am),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

// ─── Hilfen ─────────────────────────────────────────────────────────────────

/** Der Firmen-Anzeigename des angemeldeten Profils — best effort, nie blockierend. */
const eigenerFirmenname = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;
    const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
    const name = (data as { company_name?: string } | null)?.company_name;
    return name?.trim() ? name.trim() : null;
  } catch {
    // Anzeige, kein Riegel — das Anlegen haengt nicht an einer Nebensache.
    return null;
  }
};

/** Anzeigename des angemeldeten Nutzers (fuer `durchgefuehrt_von`) — best effort. */
const eigenerName = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user?.id) return null;
    const { data } = await supabase.from('profiles').select('first_name, last_name').eq('id', user.id).maybeSingle();
    const p = data as { first_name?: string; last_name?: string } | null;
    const name = ([p?.first_name, p?.last_name].filter(Boolean).join(' ') || user.email || '').trim();
    return name || null;
  } catch {
    return null;
  }
};

/** Leere Zeichenkette → null (Spalten sind NULL-bar; ein '' ist dort eine Luege). */
const leerZuNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

const istKategorie = (v: unknown): v is FristKategorie => FRIST_KATEGORIEN.some((k) => k.id === v);
const istArt = (v: unknown): v is TaetigkeitArt => TAETIGKEIT_ARTEN.some((a) => a.id === v);

/** Prueft ein ISO-Datum und liefert es auf yyyy-mm-dd gekuerzt; wirft mit Feldname. */
const pruefeDatum = (v: string | null | undefined, feld: string): string => {
  const t = parseIsoDatum(v);
  if (!t) throw new Error(`Bitte ein gültiges Datum für „${feld}" eingeben.`);
  return (v ?? '').trim().slice(0, 10);
};

const pruefeIntervall = (v: number | null | undefined): number | null => {
  if (v == null) return null;
  if (!Number.isInteger(v) || v < 1 || v > 120) {
    throw new Error('Die Wiederholung muss zwischen 1 und 120 Monaten liegen (leer = einmalig).');
  }
  return v;
};

const pruefeDauer = (v: number | null | undefined): number => {
  const n = typeof v === 'number' ? Math.round(v) : Number.NaN;
  if (!Number.isFinite(n) || n < 1 || n > DAUER_MAX_MINUTEN) {
    throw new Error(`Die Dauer muss zwischen 1 Minute und ${DAUER_MAX_MINUTEN / 60} Stunden liegen.`);
  }
  return n;
};

// ═══ Fristen ══════════════════════════════════════════════════════════════

/**
 * Alle Fristen eines Projekts — auch erledigte (Historie) — nach Faelligkeit.
 * Die Anzeige sortiert mit `sortiereFristen` (offene zuerst). Fail-open bei
 * fehlender Tabelle; RLS antwortet mit LEERER Menge, nicht mit Fehler.
 */
export const fetchFristen = async (projectId: string): Promise<SifaFrist[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  try {
    const { data, error } = await supabase
      .from(T_FRISTEN)
      .select('*')
      .eq('project_id', id)
      .order('faellig_am', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r: Zeile) => mapFrist(r));
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_fristen fehlt — Migration Nr. 92 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

/** Eine einzelne Frist; null, wenn es sie nicht gibt (oder RLS sie verbirgt). */
const fetchFrist = async (id: string): Promise<SifaFrist | null> => {
  try {
    const { data, error } = await supabase.from(T_FRISTEN).select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapFrist(data as Zeile) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

/** Legt eine Frist an. Titel, Kategorie und Datum sind Pflicht; leere Felder werden NULL. */
export const createFrist = async (projectId: string, e: NeueFrist): Promise<SifaFrist> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich keine Frist anlegen.');
  const titel = (e.titel ?? '').trim();
  if (!titel) throw new Error('Bitte einen Titel für die Frist eingeben.');
  if (!istKategorie(e.kategorie)) throw new Error('Bitte eine Kategorie wählen.');

  const zeile: Zeile = {
    project_id: pid,
    titel,
    kategorie: e.kategorie,
    faellig_am: pruefeDatum(e.faelligAm, 'Fällig am'),
    intervall_monate: pruefeIntervall(e.intervallMonate),
    mitarbeiter_id: leerZuNull(e.mitarbeiterId),
    zustaendig: leerZuNull(e.zustaendig),
    notiz: leerZuNull(e.notiz),
    company_name: await eigenerFirmenname(),
  };
  const { data, error } = await supabase.from(T_FRISTEN).insert(zeile).select('*').single();
  if (error) return werfe(error);
  return mapFrist(data as Zeile);
};

const fristPatchZuZeile = (patch: Partial<NeueFrist>): Zeile => {
  const z: Zeile = {};
  if (patch.titel !== undefined) {
    const titel = (patch.titel ?? '').trim();
    if (!titel) throw new Error('Der Titel darf nicht leer sein.');
    z.titel = titel;
  }
  if (patch.kategorie !== undefined) {
    if (!istKategorie(patch.kategorie)) throw new Error('Bitte eine Kategorie wählen.');
    z.kategorie = patch.kategorie;
  }
  if (patch.faelligAm !== undefined) z.faellig_am = pruefeDatum(patch.faelligAm, 'Fällig am');
  if (patch.intervallMonate !== undefined) z.intervall_monate = pruefeIntervall(patch.intervallMonate);
  if (patch.mitarbeiterId !== undefined) z.mitarbeiter_id = leerZuNull(patch.mitarbeiterId);
  if (patch.zustaendig !== undefined) z.zustaendig = leerZuNull(patch.zustaendig);
  if (patch.notiz !== undefined) z.notiz = leerZuNull(patch.notiz);
  return z;
};

/**
 * Aendert eine Frist (Titel, Kategorie, Datum, Wiederholung, Person, Notiz).
 * `erledigtAm` und `vorgaengerId` gehen NICHT hierueber — erledigt wird mit
 * `erledigeFrist`, der Vorgaenger entsteht nur dort.
 */
export const updateFrist = async (id: string, patch: Partial<NeueFrist>): Promise<SifaFrist> => {
  const fid = (id || '').trim();
  if (!fid) throw new Error('Diese Frist wurde nicht gefunden (Speichern).');
  const zeile = fristPatchZuZeile(patch);
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase.from(T_FRISTEN).update(zeile).eq('id', fid).select('*');
  if (error) return werfe(error);
  if (!data || data.length === 0) throw new Error('Diese Frist wurde nicht gefunden (Speichern).');
  return mapFrist(data[0] as Zeile);
};

/**
 * Erledigt eine Frist und legt bei Wiederholung die Nachfolgezeile an.
 *
 * REIHENFOLGE IST DER RIEGEL: (1) Frist lesen, (2) Nachfolgezeile anlegen,
 * (3) `erledigt_am` schreiben — nicht umgekehrt. Beide Schritte sind eigene
 * Transaktionen (PostgREST kennt keine); bricht es zwischen ihnen ab, darf
 * NIE „erledigt ohne Nachfolge" uebrig bleiben, denn das faellt niemandem
 * auf (die Frist ist ja weg) — „Nachfolge ohne Abschluss" steht dagegen
 * sichtbar als zweite offene Zeile in der Liste und heilt sich beim naechsten
 * Klick: der Insert laeuft auf den partiellen Unique-Index
 * (`vorgaenger_id`, Nr. 92) → 23505 → die vorhandene Nachfolgezeile wird
 * gelesen, dann der Abschluss geschrieben. Ein zweiter Browser sieht dasselbe.
 *
 * Ist die Frist beim Lesen schon erledigt, aber ihre Nachfolge fehlt (alter
 * Abbruch), wird sie hier nachgeholt statt „bereits erledigt" zu melden.
 *
 * Datum der Nachfolge: vom URSPRUENGLICHEN Faelligkeitsdatum aus, nicht vom
 * Erledigungstag — sonst wandert ein Turnus mit jeder spaeten Erledigung nach
 * hinten. Liegt das Ergebnis nicht hinter dem Erledigungstag (die Frist war
 * mehr als einen Turnus ueberfaellig), wird so oft weitergeschaltet, bis es
 * dahinter liegt — eine Nachfolgezeile, die schon bei Geburt rot ist, ist
 * kein Vorschlag, sondern Laerm.
 */
export const erledigeFrist = async (
  id: string,
  erledigtAm: string,
): Promise<{ erledigt: SifaFrist; naechste: SifaFrist | null }> => {
  const fid = (id || '').trim();
  if (!fid) throw new Error('Diese Frist wurde nicht gefunden (Erledigen).');
  const datum = pruefeDatum(erledigtAm, 'Erledigt am');

  // (1) Lesen — auch die Nachfolge braucht die Stammwerte der Zeile.
  const frist = await fetchFrist(fid);
  if (!frist) throw new Error('Diese Frist wurde nicht gefunden (Erledigen).');

  // (2) Nachfolge zuerst; `neu` sagt, ob diese Sitzung sie angelegt hat.
  const nachfolge = frist.intervallMonate
    ? await nachfolgeAnlegen(frist, datum)
    : { zeile: null, neu: false };

  if (frist.erledigtAm) {
    // Zweiter Browser oder Wiederholung: nur wenn die Nachfolge eben erst
    // nachgeholt wurde, war der Klick nuetzlich — sonst ist er doppelt.
    if (nachfolge.neu) return { erledigt: frist, naechste: nachfolge.zeile };
    throw new Error('Diese Frist ist bereits erledigt.');
  }

  // (3) Abschluss — nur, solange niemand schneller war.
  const { data, error } = await supabase
    .from(T_FRISTEN)
    .update({ erledigt_am: datum, updated_at: new Date().toISOString() })
    .eq('id', fid)
    .is('erledigt_am', null)
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return { erledigt: mapFrist(data[0] as Zeile), naechste: nachfolge.zeile };

  // 0 Zeilen: zwischen (1) und (3) hat ein anderer erledigt. Sein Stand gilt.
  const jetzt = await fetchFrist(fid);
  if (!jetzt) throw new Error('Diese Frist wurde nicht gefunden (Erledigen).');
  return { erledigt: jetzt, naechste: nachfolge.zeile };
};

/**
 * Legt die Nachfolgezeile einer wiederkehrenden Frist an. 23505 auf dem
 * partiellen Unique-Index `vorgaenger_id` = es gibt sie schon (zweiter
 * Browser, frueherer Abbruch) → die vorhandene Zeile ist die Antwort.
 */
const nachfolgeAnlegen = async (
  frist: SifaFrist,
  erledigtAm: string,
): Promise<{ zeile: SifaFrist | null; neu: boolean }> => {
  const intervall = frist.intervallMonate as number;
  let faelligAm = naechsteFaelligkeit(frist.faelligAm, intervall);
  for (let schritte = 0; faelligAm <= erledigtAm && schritte < 200; schritte += 1) {
    faelligAm = naechsteFaelligkeit(faelligAm, intervall);
  }

  const nachfolger: Zeile = {
    project_id: frist.projectId,
    mitarbeiter_id: frist.mitarbeiterId,
    kategorie: frist.kategorie,
    titel: frist.titel,
    faellig_am: faelligAm,
    intervall_monate: intervall,
    zustaendig: frist.zustaendig,
    notiz: frist.notiz,
    vorgaenger_id: frist.id,
    company_name: await eigenerFirmenname(),
  };
  const eingefuegt = await supabase.from(T_FRISTEN).insert(nachfolger).select('*').single();
  if (!eingefuegt.error) return { zeile: mapFrist(eingefuegt.data as Zeile), neu: true };
  if (!istUniqueVerletzung(eingefuegt.error)) return werfe(eingefuegt.error);

  const { data: vorhanden, error: leseFehler } = await supabase
    .from(T_FRISTEN)
    .select('*')
    .eq('vorgaenger_id', frist.id)
    .maybeSingle();
  if (leseFehler) return werfe(leseFehler);
  return { zeile: vorhanden ? mapFrist(vorhanden as Zeile) : null, neu: false };
};

/**
 * Loescht eine Frist. Eine Nachfolgezeile verliert dabei nur ihren Verweis
 * (`vorgaenger_id … on delete set null`), nicht ihre Existenz.
 */
export const deleteFrist = async (id: string): Promise<void> => {
  const fid = (id || '').trim();
  if (!fid) return;
  const { error } = await supabase.from(T_FRISTEN).delete().eq('id', fid);
  if (error) return werfe(error);
};

// ═══ Taetigkeiten ═════════════════════════════════════════════════════════

/**
 * Taetigkeiten eines Projekts, neueste zuerst; mit `jahr` nur das
 * Kalenderjahr. Fail-open bei fehlender Tabelle.
 */
export const fetchTaetigkeiten = async (projectId: string, jahr?: number): Promise<SifaTaetigkeit[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  try {
    let q = supabase.from(T_TAETIGKEITEN).select('*').eq('project_id', id);
    if (typeof jahr === 'number' && Number.isInteger(jahr)) {
      q = q.gte('datum', `${jahr}-01-01`).lte('datum', `${jahr}-12-31`);
    }
    const { data, error } = await q.order('datum', { ascending: false }).order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: Zeile) => mapTaetigkeit(r));
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_taetigkeiten fehlt — Migration Nr. 92 noch nicht gefahren.');
      return [];
    }
    return werfe(e);
  }
};

const fetchTaetigkeit = async (id: string): Promise<SifaTaetigkeit | null> => {
  try {
    const { data, error } = await supabase.from(T_TAETIGKEITEN).select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapTaetigkeit(data as Zeile) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

/**
 * Legt eine Taetigkeit an. Datum, Dauer (1–1440 min), Art und Titel sind
 * Pflicht. `durchgefuehrtVon` faellt auf den Anzeigenamen des angemeldeten
 * Nutzers zurueck (eingefroren — spaetere Namensaenderungen im Profil
 * aendern den Nachweis nicht); `created_by` vergibt die Datenbank.
 */
export const createTaetigkeit = async (projectId: string, e: NeueTaetigkeit): Promise<SifaTaetigkeit> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich keine Tätigkeit erfassen.');
  const titel = (e.titel ?? '').trim();
  if (!titel) throw new Error('Bitte einen Titel für die Tätigkeit eingeben.');
  if (!istArt(e.art)) throw new Error('Bitte eine Art der Tätigkeit wählen.');

  const [companyName, nutzerName] = await Promise.all([eigenerFirmenname(), leerZuNull(e.durchgefuehrtVon) ? null : eigenerName()]);
  const zeile: Zeile = {
    project_id: pid,
    datum: pruefeDatum(e.datum, 'Datum'),
    dauer_minuten: pruefeDauer(e.dauerMinuten),
    art: e.art,
    titel,
    beschreibung: leerZuNull(e.beschreibung),
    durchgefuehrt_von: leerZuNull(e.durchgefuehrtVon) ?? nutzerName,
    bezug: leerZuNull(e.bezug),
    company_name: companyName,
  };
  const { data, error } = await supabase.from(T_TAETIGKEITEN).insert(zeile).select('*').single();
  if (error) return werfe(error);
  return mapTaetigkeit(data as Zeile);
};

const taetigkeitPatchZuZeile = (patch: Partial<NeueTaetigkeit>): { zeile: Zeile; beruehrtBeleg: boolean } => {
  const z: Zeile = {};
  let beruehrtBeleg = false;
  if (patch.titel !== undefined) {
    const titel = (patch.titel ?? '').trim();
    if (!titel) throw new Error('Der Titel darf nicht leer sein.');
    z.titel = titel;
  }
  if (patch.datum !== undefined) {
    z.datum = pruefeDatum(patch.datum, 'Datum');
    beruehrtBeleg = true;
  }
  if (patch.dauerMinuten !== undefined) {
    z.dauer_minuten = pruefeDauer(patch.dauerMinuten);
    beruehrtBeleg = true;
  }
  if (patch.art !== undefined) {
    if (!istArt(patch.art)) throw new Error('Bitte eine Art der Tätigkeit wählen.');
    z.art = patch.art;
    beruehrtBeleg = true;
  }
  if (patch.beschreibung !== undefined) z.beschreibung = leerZuNull(patch.beschreibung);
  if (patch.durchgefuehrtVon !== undefined) z.durchgefuehrt_von = leerZuNull(patch.durchgefuehrtVon);
  if (patch.bezug !== undefined) z.bezug = leerZuNull(patch.bezug);
  return { zeile: z, beruehrtBeleg };
};

/**
 * Aendert eine Taetigkeit. Titel, Beschreibung, Wer und Bezug immer; Datum,
 * Dauer und Art NUR solange `gebucht_am` null ist (Riegel (b)) — das Update
 * traegt dann `.is('gebucht_am', null)` und null Zeilen heisst „gebucht".
 */
export const updateTaetigkeit = async (id: string, patch: Partial<NeueTaetigkeit>): Promise<SifaTaetigkeit> => {
  const tid = (id || '').trim();
  if (!tid) throw new Error('Diese Tätigkeit wurde nicht gefunden (Speichern).');
  const { zeile, beruehrtBeleg } = taetigkeitPatchZuZeile(patch);
  zeile.updated_at = new Date().toISOString();

  let q = supabase.from(T_TAETIGKEITEN).update(zeile).eq('id', tid);
  if (beruehrtBeleg) q = q.is('gebucht_am', null);
  const { data, error } = await q.select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return mapTaetigkeit(data[0] as Zeile);

  const vorhanden = await fetchTaetigkeit(tid);
  if (!vorhanden) throw new Error('Diese Tätigkeit wurde nicht gefunden (Speichern).');
  if (vorhanden.gebuchtAm) {
    throw new Error('Diese Tätigkeit ist bereits abgerechnet — Datum, Dauer und Art lassen sich nicht mehr ändern.');
  }
  throw new Error('Die Tätigkeit konnte nicht gespeichert werden (Speichern).');
};

/**
 * Loescht eine Taetigkeit — nur solange `gebucht_am` null ist (Riegel (b)).
 * Eine gebuchte wirft; eine nicht (mehr) vorhandene ist kein Fehler (der
 * zweite Klick ist der Normalfall).
 */
export const deleteTaetigkeit = async (id: string): Promise<void> => {
  const tid = (id || '').trim();
  if (!tid) return;
  const { data, error } = await supabase
    .from(T_TAETIGKEITEN)
    .delete()
    .eq('id', tid)
    .is('gebucht_am', null)
    .select('id');
  if (error) return werfe(error);
  if (data && data.length > 0) return;

  const vorhanden = await fetchTaetigkeit(tid);
  if (vorhanden?.gebuchtAm) throw new Error('Diese Tätigkeit ist bereits abgerechnet und lässt sich nicht löschen.');
};

// ═══ Kennzahlen ═══════════════════════════════════════════════════════════

/** head-Count — es wird keine Zeile uebertragen. Fail-open: fehlende Tabelle = 0. */
const zaehle = async (tabelle: string, filter: (q: any) => any): Promise<number> => {
  try {
    const { count, error } = await filter(supabase.from(tabelle).select('id', { count: 'exact', head: true }));
    if (error) throw error;
    return typeof count === 'number' ? count : 0;
  } catch (e) {
    if (istFehlendesSchema(e)) return 0;
    return werfe(e);
  }
};

/**
 * Kennzahlen fuer Kachel und Betreuungskarte (Bauplan §1): zwei head-Counts
 * (ueberfaellig, bald), die naechste offene Frist (`order faellig_am limit
 * 1`) und die Jahressumme — `select dauer_minuten` des Kalenderjahres,
 * clientseitig summiert (keine RPC; ein Jahr Betreuung sind ein paar hundert
 * Zeilen mit je einer Zahl). Dieselben Grenzen wie `fristAmpel`: rot
 * < heute, bald bis einschliesslich heute + 60 Tage. Fail-open: alles 0/null.
 */
export const ladeKennzahlen = async (projectId: string, heute: Date = new Date()): Promise<SifaKennzahlen> => {
  const pid = (projectId || '').trim();
  if (!pid) return { ...KENNZAHLEN_LEER };
  const heuteIso = isoDatum(heute);
  const baldBis = plusTage(heuteIso, AMPEL_GELB_TAGE);
  const jahr = heute.getFullYear();

  const naechsteFrist = async (): Promise<SifaKennzahlen['naechsteFrist']> => {
    try {
      const { data, error } = await supabase
        .from(T_FRISTEN)
        .select('titel, faellig_am')
        .eq('project_id', pid)
        .is('erledigt_am', null)
        .order('faellig_am', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const z = data as { titel?: unknown; faellig_am?: unknown } | null;
      return z ? { titel: str(z.titel), faelligAm: iso(z.faellig_am) } : null;
    } catch (e) {
      if (istFehlendesSchema(e)) return null;
      return werfe(e);
    }
  };

  const jahresSumme = async (): Promise<{ minuten: number; anzahl: number }> => {
    try {
      const { data, error } = await supabase
        .from(T_TAETIGKEITEN)
        .select('dauer_minuten')
        .eq('project_id', pid)
        .gte('datum', `${jahr}-01-01`)
        .lte('datum', `${jahr}-12-31`);
      if (error) throw error;
      const zeilen = (data ?? []) as Array<{ dauer_minuten?: unknown }>;
      return { minuten: zeilen.reduce((s, z) => s + Math.max(0, num(z.dauer_minuten, 0)), 0), anzahl: zeilen.length };
    } catch (e) {
      if (istFehlendesSchema(e)) return { minuten: 0, anzahl: 0 };
      return werfe(e);
    }
  };

  const [ueberfaellig, bald, naechste, summe] = await Promise.all([
    zaehle(T_FRISTEN, (q) => q.eq('project_id', pid).is('erledigt_am', null).lt('faellig_am', heuteIso)),
    zaehle(T_FRISTEN, (q) => q.eq('project_id', pid).is('erledigt_am', null).gte('faellig_am', heuteIso).lte('faellig_am', baldBis)),
    naechsteFrist(),
    jahresSumme(),
  ]);

  return {
    fristenUeberfaellig: ueberfaellig,
    fristenBald: bald,
    naechsteFrist: naechste,
    minutenJahr: summe.minuten,
    taetigkeitenJahr: summe.anzahl,
  };
};

// ═══ Kachel-Aktivitaet ════════════════════════════════════════════════════

/** Juengstes updated_at einer Tabelle je Projekt; null bei leer oder fehlender Tabelle. */
const maxUpdatedAt = async (tabelle: string, projectId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from(tabelle)
      .select('updated_at')
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return strOrNull((data as { updated_at?: unknown } | null)?.updated_at);
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

/**
 * Letzte Aktivitaet je Kachel (ModuleActivityKey 'fristen' | 'taetigkeiten' |
 * 'gbu', services/moduleActivity.ts). Drei kleine Abfragen — fail-open,
 * solange die Tabellen fehlen (`sifa_gbu` erst ab Nr. 167). Bei der GBU zaehlt
 * nur der Kopf: Gefaehrdungen und Massnahmen aendern `sifa_gbu.updated_at`
 * nicht, eine Abfrage je Kindtabelle waere fuer eine Kachelzeile zu teuer.
 */
export const letzteBetreuungAktivitaet = async (
  projectId: string,
): Promise<{ fristen: string | null; taetigkeiten: string | null; gbu: string | null }> => {
  const id = (projectId || '').trim();
  if (!id) return { fristen: null, taetigkeiten: null, gbu: null };
  const [fristen, taetigkeiten, gbu] = await Promise.all([
    maxUpdatedAt(T_FRISTEN, id), maxUpdatedAt(T_TAETIGKEITEN, id), maxUpdatedAt('sifa_gbu', id),
  ]);
  return { fristen, taetigkeiten, gbu };
};
